import React, { useEffect, useState } from "react";
import {
  Row, Col, Card, Table, Button, Input, Modal, Form,
  notification, Space, Typography, Tag, Popconfirm, Switch,
} from "antd";
import {
  PlusOutlined, AppstoreOutlined, TagsOutlined, ExclamationCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { baseUrl } from "../../config";

const { Title, Text } = Typography;
const { Item } = Form;

function SectorList() {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(false);

  // Sector modal state
  const [sectorModalOpen, setSectorModalOpen] = useState(false);
  const [sectorForm] = Form.useForm();
  const [editingSector, setEditingSector] = useState(null);
  const [sectorSaving, setSectorSaving] = useState(false);

  // Sub-sector modal state
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [subForm] = Form.useForm();
  const [editingSub, setEditingSub] = useState(null);
  const [activeSectorId, setActiveSectorId] = useState(null);
  const [activeSectorName, setActiveSectorName] = useState('');
  const [subSaving, setSubSaving] = useState(false);

  useEffect(() => {
    fetchSectors();
  }, []);

  const fetchSectors = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/business-sector/list`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (res.data.status) {
        setSectors(res.data.result || []);
      }
    } catch {
      notification.error({ message: 'Error', description: 'Could not load sectors', placement: 'topRight' });
    } finally {
      setLoading(false);
    }
  };

  // ── Sector CRUD ────────────────────────────────────────────────────────────
  const openAddSector = () => {
    setEditingSector(null);
    sectorForm.resetFields();
    setSectorModalOpen(true);
  };

  const openEditSector = (record) => {
    setEditingSector(record);
    sectorForm.setFieldsValue({ name: record.name, sort_order: record.sort_order });
    setSectorModalOpen(true);
  };

  const saveSector = async () => {
    try {
      const values = await sectorForm.validateFields();
      setSectorSaving(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };

      if (editingSector) {
        await axios.patch(`${baseUrl}/business-sector/update/${editingSector.id}`, values, { headers });
        notification.success({ message: 'Updated', description: 'Sector updated successfully', placement: 'topRight' });
      } else {
        await axios.post(`${baseUrl}/business-sector/create`, values, { headers });
        notification.success({ message: 'Added', description: 'Sector added successfully', placement: 'topRight' });
      }
      setSectorModalOpen(false);
      fetchSectors();
    } catch (err) {
      if (err?.errorFields) return;
      notification.error({ message: 'Error', description: err?.response?.data?.message || 'Save failed', placement: 'topRight' });
    } finally {
      setSectorSaving(false);
    }
  };

  const toggleSector = async (id) => {
    try {
      await axios.patch(`${baseUrl}/business-sector/toggle/${id}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      fetchSectors();
    } catch (err) {
      notification.error({ message: 'Error', description: err?.response?.data?.message || 'Toggle failed', placement: 'topRight' });
    }
  };

  const toggleSub = async (id) => {
    try {
      await axios.patch(`${baseUrl}/business-sector/sub/toggle/${id}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      fetchSectors();
    } catch (err) {
      notification.error({ message: 'Error', description: err?.response?.data?.message || 'Toggle failed', placement: 'topRight' });
    }
  };

  const deleteSector = async (id) => {
    try {
      await axios.delete(`${baseUrl}/business-sector/delete/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      notification.success({ message: 'Deleted', description: 'Sector deleted', placement: 'topRight' });
      fetchSectors();
    } catch (err) {
      notification.error({ message: 'Error', description: err?.response?.data?.message || 'Delete failed', placement: 'topRight' });
    }
  };

  // ── Sub-sector CRUD ────────────────────────────────────────────────────────
  const openAddSub = (sector) => {
    setEditingSub(null);
    setActiveSectorId(sector.id);
    setActiveSectorName(sector.name);
    subForm.resetFields();
    setSubModalOpen(true);
  };

  const openEditSub = (sub, sector) => {
    setEditingSub(sub);
    setActiveSectorId(sector.id);
    setActiveSectorName(sector.name);
    subForm.setFieldsValue({ name: sub.name, sort_order: sub.sort_order });
    setSubModalOpen(true);
  };

  const saveSub = async () => {
    try {
      const values = await subForm.validateFields();
      setSubSaving(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
      const payload = { ...values, sector_id: activeSectorId };

      if (editingSub) {
        await axios.patch(`${baseUrl}/business-sector/sub/update/${editingSub.id}`, payload, { headers });
        notification.success({ message: 'Updated', description: 'Sub-sector updated', placement: 'topRight' });
      } else {
        await axios.post(`${baseUrl}/business-sector/sub/create`, payload, { headers });
        notification.success({ message: 'Added', description: 'Sub-sector added', placement: 'topRight' });
      }
      setSubModalOpen(false);
      fetchSectors();
    } catch (err) {
      if (err?.errorFields) return;
      notification.error({ message: 'Error', description: err?.response?.data?.message || 'Save failed', placement: 'topRight' });
    } finally {
      setSubSaving(false);
    }
  };

  const deleteSub = async (subId) => {
    try {
      await axios.delete(`${baseUrl}/business-sector/sub/delete/${subId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      notification.success({ message: 'Deleted', description: 'Sub-sector deleted', placement: 'topRight' });
      fetchSectors();
    } catch (err) {
      notification.error({ message: 'Error', description: err?.response?.data?.message || 'Delete failed', placement: 'topRight' });
    }
  };

  // ── Sub-sector expanded table ──────────────────────────────────────────────
  const expandedRowRender = (sector) => {
    const subCols = [
      {
        title: '#',
        dataIndex: 'sort_order',
        key: 'sort_order',
        width: 60,
        render: (v, _, i) => v ?? i + 1,
      },
      {
        title: 'Sub-sector Name',
        dataIndex: 'name',
        key: 'name',
        render: (name, sub) => (
          <Tag color={sub.status === 1 ? 'blue' : 'default'} style={{ fontSize: 13 }}>{name}</Tag>
        ),
      },
      {
        title: 'Active',
        key: 'active',
        width: 80,
        render: (_, sub) => (
          <Switch
            checked={sub.status === 1}
            onChange={() => toggleSub(sub.id)}
            checkedChildren="ON" unCheckedChildren="OFF"
            size="small"
          />
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 160,
        render: (_, sub) => (
          <Space size={6}>
            <Button size="small" onClick={() => openEditSub(sub, sector)}
              style={{ borderColor: '#7a0000', color: '#7a0000', borderRadius: 8, fontWeight: 600, minWidth: 54 }}>
              Edit
            </Button>
            <Popconfirm
              title="Delete this sub-sector?"
              onConfirm={() => deleteSub(sub.id)}
              okText="Yes" cancelText="No" okType="danger"
              icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
            >
              <Button size="small" danger style={{ borderRadius: 8, fontWeight: 600, minWidth: 64 }}>
                Remove
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];

    return (
      <div style={{ margin: '0 24px 12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Text strong style={{ color: '#7a0000', fontSize: 13 }}>
            <TagsOutlined style={{ marginRight: 6 }} />
            Sub-sectors of: <span style={{ color: '#7a0000' }}>{sector.name}</span>
            <Tag style={{ marginLeft: 8 }} color="default">{(sector.sub_sectors || []).length} total</Tag>
          </Text>
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openAddSub(sector)}
            style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 6 }}
          >
            Add Sub-sector
          </Button>
        </div>
        <Table
          columns={subCols}
          dataSource={(sector.sub_sectors || []).map((s, i) => ({ ...s, key: s.id || i }))}
          pagination={false}
          size="small"
          bordered
          locale={{ emptyText: 'No sub-sectors yet. Click "Add Sub-sector" to add.' }}
        />
      </div>
    );
  };

  // ── Main sector columns ────────────────────────────────────────────────────
  const sectorCols = [
    {
      title: '#',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 60,
      render: (v, _, i) => v ?? i + 1,
    },
    {
      title: 'Main Sector Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Text strong style={{ fontSize: 14, color: record.status === 1 ? '#7a0000' : '#aaa' }}>{name}</Text>
      ),
    },
    {
      title: 'Sub-sectors',
      dataIndex: 'sub_sectors',
      key: 'sub_sectors',
      render: (subs) => {
        const active = (subs || []).filter(s => s.status === 1).length;
        const total  = (subs || []).length;
        return (
          <span>
            <Tag color="geekblue">{total} total</Tag>
            {active < total && <Tag color="green">{active} active</Tag>}
          </span>
        );
      },
    },
    {
      title: 'Active',
      key: 'active',
      width: 90,
      render: (_, record) => (
        <Switch
          checked={record.status === 1}
          onChange={() => toggleSector(record.id)}
          checkedChildren="ON" unCheckedChildren="OFF"
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space size={6}>
          <Button size="small" onClick={() => openEditSector(record)}
            style={{ borderColor: '#7a0000', color: '#7a0000', borderRadius: 8, fontWeight: 600, minWidth: 54 }}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this sector and all its sub-sectors?"
            onConfirm={() => deleteSector(record.id)}
            okText="Yes, Delete" cancelText="No" okType="danger"
            icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
          >
            <Button size="small" danger style={{ borderRadius: 8, fontWeight: 600, minWidth: 64 }}>
              Remove
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title={
              <span style={{ color: 'white', fontWeight: 600, fontSize: 16 }}>
                <AppstoreOutlined style={{ marginRight: 8 }} />
                Business Directory — Sector Management
              </span>
            }
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0', borderRadius: '8px 8px 0 0'}}
            extra={
              <Button
                icon={<PlusOutlined />}
                onClick={openAddSector}
                style={{ background: '#ffffff', borderColor: '#ffffff', color: '#7a0000', borderRadius: 8, fontWeight: 700 }}
              >
                Add Main Sector
              </Button>
            }
          >
            <div style={{ padding: '12px 24px 0' }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Expand a row to manage its sub-sectors. Sub-sectors appear in the public directory form as a dropdown.
              </Text>
            </div>

            <Table
              columns={sectorCols}
              dataSource={sectors.map((s, i) => ({ ...s, key: s.id || i }))}
              loading={loading}
              expandable={{ expandedRowRender, expandRowByClick: false, rowExpandable: () => true }}
              pagination={{ pageSize: 20 }}
              style={{ padding: '12px 24px' }}
              size="middle"
              bordered={false}
              locale={{ emptyText: 'No sectors found. Click "Add Main Sector" to begin.' }}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Add / Edit Sector Modal ─────────────────────────────────────── */}
      <Modal
        title={
          <span style={{ fontWeight: 700, color: '#7a0000' }}>
            <AppstoreOutlined style={{ marginRight: 8 }} />
            {editingSector ? 'Edit Main Sector' : 'Add New Main Sector'}
          </span>
        }
        open={sectorModalOpen}
        onOk={saveSector}
        onCancel={() => setSectorModalOpen(false)}
        okText={editingSector ? 'Update' : 'Save'}
        confirmLoading={sectorSaving}
        okButtonProps={{ style: { background: '#7a0000', borderColor: '#7a0000' } }}
        destroyOnClose
      >
        <Form form={sectorForm} layout="vertical" style={{ marginTop: 16 }}>
          <Item
            label="Main Sector Name"
            name="name"
            rules={[{ required: true, message: 'Please enter sector name' }]}
          >
            <Input placeholder="e.g. कपड़ा एवं फैशन" size="large" />
          </Item>
          <Item label="Sort Order" name="sort_order">
            <Input type="number" placeholder="e.g. 1, 2, 3..." size="large" min={1} />
          </Item>
        </Form>
      </Modal>

      {/* ── Add / Edit Sub-sector Modal ─────────────────────────────────── */}
      <Modal
        title={
          <span style={{ fontWeight: 700, color: '#7a0000' }}>
            <TagsOutlined style={{ marginRight: 8 }} />
            {editingSub ? 'Edit Sub-sector' : 'Add Sub-sector'} —{' '}
            <span style={{ color: '#7a0000', fontSize: 13 }}>{activeSectorName}</span>
          </span>
        }
        open={subModalOpen}
        onOk={saveSub}
        onCancel={() => setSubModalOpen(false)}
        okText={editingSub ? 'Update' : 'Save'}
        confirmLoading={subSaving}
        okButtonProps={{ style: { background: '#7a0000', borderColor: '#7a0000' } }}
        destroyOnClose
      >
        <Form form={subForm} layout="vertical" style={{ marginTop: 16 }}>
          <Item
            label="Sub-sector Name"
            name="name"
            rules={[{ required: true, message: 'Please enter sub-sector name' }]}
          >
            <Input placeholder="e.g. साड़ी, बुटीक, गारमेंट्स" size="large" />
          </Item>
          <Item label="Sort Order" name="sort_order">
            <Input type="number" placeholder="e.g. 1, 2, 3..." size="large" min={1} />
          </Item>
        </Form>
      </Modal>
    </div>
  );
}

export default SectorList;
